import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";

interface Comment {
  _id: string;
  body: string;
  createdAt: number;
  isMe: boolean;
  author: {
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
}

// Comment thread for a single task. Anyone with a property
// membership can post + read. The parent dialog also fires
// `comments.markSeen` on open so the unread badge clears.
export function CommentsSection({ taskId }: { taskId: string }) {
  const comments = useQuery(anyApi.comments.listForTask, { taskId }) as
    | Comment[]
    | undefined;
  const add = useMutation(anyApi.comments.add);
  const markSeen = useMutation(anyApi.comments.markSeen);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mark seen on mount + whenever the comment list grows while open
  // (so a stream of replies coming in doesn't leave a stale unread).
  useEffect(() => {
    if (comments === undefined) return;
    markSeen({ taskId }).catch(() => {});
  }, [taskId, comments?.length, markSeen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await add({ taskId, body });
      setBody("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section>
      <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
        Comments
      </h3>
      {comments === undefined ? (
        <p className="mt-3 text-sm text-slate-500">Loading…</p>
      ) : comments.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No comments yet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {comments.map((c) => (
            <li
              key={c._id}
              className={`rounded-md border px-3 py-2 ${
                c.isMe
                  ? "border-slate-300 bg-slate-50"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-slate-700">
                  {c.isMe ? "You" : authorName(c.author)}
                </span>
                <span className="text-xs text-slate-400">
                  {timeAgo(c.createdAt)}
                </span>
              </div>
              <p className="mt-0.5 text-sm text-slate-700 whitespace-pre-wrap">
                {c.body}
              </p>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={handleSubmit} className="mt-3 flex items-start gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder="Add a comment…"
          className="flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 resize-none"
        />
        <button
          type="submit"
          disabled={submitting || !body.trim()}
          className="self-end rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {submitting ? "…" : "Post"}
        </button>
      </form>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </section>
  );
}

function authorName(a: Comment["author"]): string {
  if (!a) return "Unknown";
  if (a.firstName) {
    return a.lastName ? `${a.firstName} ${a.lastName.charAt(0)}.` : a.firstName;
  }
  return a.email.split("@")[0];
}

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
